/**
 * Frontend prediction utilities for account and fund suggestions
 * Based on historical contact usage patterns
 */

/**
 * Determines if ML predictions are enabled for an org.
 * Admin override (mlOverride) takes priority over subscription feature flag.
 *
 * @param {object} org - The org document data
 * @returns {boolean} Whether ML predictions are enabled
 */
export function isMlEnabled(org) {
  if (org?.mlOverride === "on") return true;
  if (org?.mlOverride === "off") return false;
  return org?.features?.mlAutoCategorization === true;
}

/**
 * Determines the ML prediction readiness state for an org.
 * Used to decide which banner/messaging to show users.
 *
 * @param {object} mlModelState - The mlModelState doc from organizationAndSettings
 * @returns {"ready" | "learning" | "unknown"} The readiness state
 *   - "ready": Model is trained and serving predictions
 *   - "learning": Org has insufficient data/variety/history — still collecting
 *   - "unknown": No mlModelState doc yet (org is brand new or hasn't been checked)
 */
export function getMlReadiness(mlModelState) {
  if (!mlModelState) return "unknown";
  const status = mlModelState.bqmlStatus;
  if (status === "trained") return "ready";
  if (
    status === "insufficient_data" ||
    status === "insufficient_variety" ||
    status === "insufficient_history"
  ) {
    return "learning";
  }
  return "unknown";
}

/**
 * Returns true if ML predictions can actively run for this org
 * (feature enabled AND model is trained).
 */
export function canRunMlPredictions(orgData, mlModelState) {
  return isMlEnabled(orgData) && getMlReadiness(mlModelState) === "ready";
}

// Matches the Tier 1 lookup threshold in runAccountFundPrediction
// (both frontend and backend require bestMatchCount >= 5 to fire).
// Exported for the contactAccountFundMatches subcollection readiness query
// (context_and_index) and mirrored in functions/transactions/shared/
// contactMatches.js, which denormalizes maxMatchCount against it.
export const LOOKUP_PREDICTION_MIN_COUNT = 5;

/**
 * Returns true if the contactAccountFundMatches doc contains at least one
 * contact whose best account or fund count meets the lookup prediction
 * threshold. Orgs that just started using the app have no such data yet,
 * so any prediction call for them is guaranteed to return nothing.
 */
export function orgHasLookupPredictiveData(contactAccountFundMatches) {
  if (!contactAccountFundMatches) return false;
  for (const contactId of Object.keys(contactAccountFundMatches)) {
    const matches = contactAccountFundMatches[contactId];
    if (!matches) continue;

    const maxAccount = matches.accounts
      ? Math.max(0, ...Object.values(matches.accounts))
      : 0;
    if (maxAccount >= LOOKUP_PREDICTION_MIN_COUNT) return true;

    const maxFund = matches.funds
      ? Math.max(0, ...Object.values(matches.funds))
      : 0;
    if (maxFund >= LOOKUP_PREDICTION_MIN_COUNT) return true;
  }
  return false;
}

/**
 * Returns true if any prediction source can actually produce a result
 * for this org. Three tiers are considered:
 *  - Tier 0: at least one open expectedTransactions doc (e.g., a
 *    payroll self-clearing item awaiting its bank draw)
 *  - Tier 1: historical contact-account-fund matches above threshold
 *  - Tier 2: trained ML model
 *
 * Used to gate the "wait for prediction" UI lock on data-entry
 * screens — new orgs with no history AND no expected items should
 * skip the lock entirely rather than wait for a round-trip that will
 * always come back empty.
 */
export function canRunPredictions(
  orgData,
  mlModelState,
  contactAccountFundMatches,
  hasOpenExpectedTransactions = false,
) {
  if (hasOpenExpectedTransactions) return true;
  if (canRunMlPredictions(orgData, mlModelState)) return true;
  if (orgHasLookupPredictiveData(contactAccountFundMatches)) return true;
  return false;
}

/**
 * Runs account and fund prediction for a contact based on historical data
 * Similar to backend runAccountFundPrediction but for frontend use
 *
 * @param {string} contactId - The contact ID to predict for
 * @param {string} excludeAccountId - Account to exclude from predictions (usually the register account)
 * @param {object} contactAccountFundMatches - Historical usage data from Firebase
 * @param {object} currentData - Current transaction data to check for user modifications
 * @returns {object} Prediction results with account/fund IDs and confidence flags
 */
export const runAccountFundPrediction = (
  contactId,
  excludeAccountId,
  contactAccountFundMatches,
  currentData = {},
) => {
  console.log("🔮 Running frontend prediction for contact:", contactId);

  if (!contactId || !contactAccountFundMatches[contactId]) {
    console.log("🔮 No historical data for contact:", contactId);
    return {
      predictions: {},
      confidence: {},
    };
  }

  const matchesForContact = contactAccountFundMatches[contactId];
  const predictions = {};
  const confidence = {};

  // Process account matches
  if (matchesForContact.accounts) {
    const accountEntries = Object.entries(matchesForContact.accounts).filter(
      ([accId]) => accId !== excludeAccountId,
    );

    if (accountEntries.length > 0) {
      const sortedAccountMatches = accountEntries.sort((a, b) => b[1] - a[1]);
      const bestMatchAccountId = sortedAccountMatches[0][0];
      const bestMatchAccountCount = sortedAccountMatches[0][1];
      const totalAccountUses = sortedAccountMatches.reduce(
        (acc, match) => acc + match[1],
        0,
      );
      const accountFraction = bestMatchAccountCount / totalAccountUses;

      console.log("🔮 Account prediction:", {
        bestMatchAccountId,
        bestMatchAccountCount,
        accountFraction,
        confidence: accountFraction >= 0.75 && bestMatchAccountCount >= 5,
      });

      // Higher threshold for better predictions (75% confidence, minimum 5 uses)
      if (
        accountFraction >= 0.75 &&
        bestMatchAccountCount >= 5 &&
        !currentData.accountUserModified
      ) {
        predictions.account = bestMatchAccountId;
        confidence.account = {
          predicted: true,
          confidence: accountFraction,
          usageCount: bestMatchAccountCount,
          totalUses: totalAccountUses,
        };
      }
    }
  }

  // If Tier 1 frequency threshold not met, check ML prediction (Tier 2 - BQML).
  // Note: Backend uses 0.85 threshold for high-amount transactions (>=$10k).
  // Frontend doesn't have amount context here, so uses the default 0.75 threshold.
  // High-amount transactions go through the backend predictTransactionAccount path.
  if (!predictions.account && matchesForContact?.mlPrediction?.account && !currentData.accountUserModified) {
    const mlPred = matchesForContact.mlPrediction;
    if (mlPred.confidence >= 0.75) {
      predictions.account = mlPred.account;
      confidence.account = {
        predicted: true,
        confidence: mlPred.confidence,
        source: "bqml",
      };
    }
  }

  // Process fund matches
  if (matchesForContact.funds) {
    const fundEntries = Object.entries(matchesForContact.funds);

    if (fundEntries.length > 0) {
      const sortedFundMatches = fundEntries.sort((a, b) => b[1] - a[1]);
      const bestMatchFundId = sortedFundMatches[0][0];
      const bestMatchFundCount = sortedFundMatches[0][1];
      const totalFundUses = sortedFundMatches.reduce(
        (acc, match) => acc + match[1],
        0,
      );
      const fundFraction = bestMatchFundCount / totalFundUses;

      console.log("🔮 Fund prediction:", {
        bestMatchFundId,
        bestMatchFundCount,
        fundFraction,
        confidence: fundFraction >= 0.75 && bestMatchFundCount >= 5,
      });

      // Higher threshold for better predictions (75% confidence, minimum 5 uses)
      if (
        fundFraction >= 0.75 &&
        bestMatchFundCount >= 5 &&
        !currentData.fundUserModified
      ) {
        predictions.fund = bestMatchFundId;
        confidence.fund = {
          predicted: true,
          confidence: fundFraction,
          usageCount: bestMatchFundCount,
          totalUses: totalFundUses,
        };
      }
    }
  }

  console.log("🔮 Prediction results:", { predictions, confidence });

  return {
    predictions,
    confidence,
  };
};

/**
 * Helper function to determine if a field should show an "Auto" badge
 *
 * @param {string} lineId - Line identifier (e.g., "0", "1")
 * @param {string} field - Field name ("account" or "fund")
 * @param {object} fieldPredictions - Current field predictions state
 * @param {object} userModifiedFields - User modification tracking state
 * @returns {boolean} Whether to show the Auto badge
 */
export const shouldShowAutoBadge = (
  lineId,
  field,
  fieldPredictions,
  userModifiedFields,
) => {
  const lineKey = `${lineId}-${field}`;
  return fieldPredictions[lineKey] && !userModifiedFields[lineKey];
};

/**
 * Helper function to determine if a field should show a "Manual" badge
 *
 * @param {string} lineId - Line identifier (e.g., "0", "1")
 * @param {string} field - Field name ("account" or "fund")
 * @param {object} userModifiedFields - User modification tracking state
 * @returns {boolean} Whether to show the Manual badge
 */
export const shouldShowManualBadge = (lineId, field, userModifiedFields) => {
  const lineKey = `${lineId}-${field}`;
  return userModifiedFields[lineKey];
};

/**
 * Helper function to apply predictions to transaction data
 *
 * @param {object} txEntryData - Current transaction entry data
 * @param {object} predictions - Predictions to apply
 * @param {string} lineId - Line identifier to apply predictions to
 * @returns {object} Updated transaction entry data
 */
export const applyPredictions = (txEntryData, predictions, lineId) => {
  const newEntryData = { ...txEntryData };

  if (predictions.account || predictions.fund) {
    // Clone the nested line object to avoid mutating React state
    newEntryData[lineId] = { ...(newEntryData[lineId] || {}) };
  }

  if (predictions.account) {
    newEntryData[lineId].account = predictions.account;
  }

  if (predictions.fund) {
    newEntryData[lineId].fund = predictions.fund;
  }

  return newEntryData;
};

/**
 * Helper function to clear predictions when contact changes
 *
 * @param {string} lineId - Line identifier
 * @param {object} fieldPredictions - Current field predictions state
 * @param {function} setFieldPredictions - State setter for field predictions
 * @param {function} setUserModifiedFields - State setter for user modifications
 */
export const clearPredictionsForLine = (
  lineId,
  fieldPredictions,
  setFieldPredictions,
  setUserModifiedFields,
) => {
  const newFieldPredictions = { ...fieldPredictions };
  const accountKey = `${lineId}-account`;
  const fundKey = `${lineId}-fund`;

  delete newFieldPredictions[accountKey];
  delete newFieldPredictions[fundKey];

  setFieldPredictions(newFieldPredictions);

  // Also clear user modifications for this line to start fresh
  setUserModifiedFields((prev) => {
    const newUserModified = { ...prev };
    delete newUserModified[accountKey];
    delete newUserModified[fundKey];
    return newUserModified;
  });
};

// ============================================================================
// ML PREDICTION HELPERS
// ============================================================================

/**
 * Returns badge display info for ML prediction confidence
 *
 * @param {string} source - Prediction source: "lookup", "ml_org", "plaid_category_map"
 * @param {number} confidence - Confidence score 0.0-1.0
 * @returns {object} { variant, color, label, tooltip }
 */
export const getConfidenceBadge = (source, confidence) => {
  if (!source) return null;

  if (source === "lookup") {
    return {
      variant: "filled",
      color: "success",
      label: "Auto",
      tooltip: `Predicted from contact history (${Math.round(confidence * 100)}% match)`,
    };
  }

  if (source === "ml_org" || source === "bqml") {
    const isHigh = confidence >= 0.85;
    return {
      variant: isHigh ? "filled" : "outlined",
      color: isHigh ? "success" : "info",
      label: "ML",
      tooltip: `ML prediction (${Math.round(confidence * 100)}% confidence)`,
    };
  }

  if (source === "plaid_category_map") {
    return {
      variant: "outlined",
      color: "default",
      label: "Suggested",
      tooltip: "Based on Plaid transaction category",
    };
  }

  return null;
};

/**
 * Builds prediction metadata for feedback tracking on a transaction line
 *
 * @param {object} mlResult - Result from predictTransactionAccount Cloud Function
 * @param {number} latencyMs - Time taken for prediction
 * @returns {object} Metadata object to attach to the transaction line
 */
export const buildPredictionMetadata = (mlResult, latencyMs) => {
  if (!mlResult || !mlResult.shouldPredict) return null;

  return {
    predictedAccount: mlResult.account,
    predictedFund: mlResult.fund || null,
    predictionConfidence: mlResult.confidence,
    predictionSource: mlResult.source,
    predictionLatencyMs: latencyMs || mlResult.predictionLatencyMs || 0,
    predictionTimestamp: Date.now(),
  };
};

/**
 * Computes override metadata when user changes a predicted field
 *
 * @param {object} predictionMetadata - The prediction metadata from buildPredictionMetadata
 * @param {string} field - "account" or "fund"
 * @param {string} newValue - The value user changed to
 * @returns {object} Override metadata to merge into the line
 */
export const buildOverrideMetadata = (predictionMetadata, field, newValue) => {
  if (!predictionMetadata) return {};

  const result = {
    wasPredictionAccepted: false,
    userCorrectedAt: new Date().toISOString(),
    timeToOverrideMs: Date.now() - (predictionMetadata.predictionTimestamp || Date.now()),
  };

  if (field === "account") {
    result.userCorrectedAccount = newValue;
  } else if (field === "fund") {
    result.userCorrectedFund = newValue;
  }

  return result;
};
